// buildSitrepPayload.js — MINSKY-16407: "Generate Situation Report" payload builder (Phase 1,
// log-only). Assembles a curated, LLM-ready JSON payload scoped to one map embed's current view
// (bbox) + selected DTG. This module has ZERO network/LLM/ProseMirror-mutation concerns — it is
// a pure function; the caller (SitrepButton.jsx/MapNodeView.js) is solely responsible for the one
// `console.log(...)` this phase requires.
//
// MINSKY-16404 payload-shape overhaul: rewritten per a design review that audited this builder's
// JSON output against LLM-token-cost and structured-input-reliability research (cut an estimated
// ~37% of tokens on a 1-unit sample; the gap widens with more units/events). This is a
// payload-SHAPE refactor — what gets computed stays the same in spirit, only what shape it's
// emitted in changes. Per-point summary of what changed (full review text lives in the ticket):
//   1. IDs — `markingId` (36-char UUID) no longer repeats on every unit/event; each marking gets
//      a short `ref` ("U1", "U2", ...) stable within one call, with a trailing `idMap` for
//      backend correlation. See `assignRef` below.
//   2. Dates — epoch-ms fields replaced with ISO-8601 (`report.generatedIso`/`asOfIso`, event
//      `ts`) plus a pre-formatted military DTG string for the report header (`report.asOfDtg`)
//      AND for every change event (`changes.events[].dtg`) — see militaryDtg.js. Both are the
//      exact same `epochMsToMilitaryDtg` conversion, so the LLM never needs to (unreliably)
//      recompute DTG notation from an ISO string itself — see this function's own doc comment
//      at the `events` mapping below for the bug this closes.
//   3. Coordinates — GeoJSON `{type, coordinates:[lon,lat]}` replaced with flat `lat`/`lon`
//      (5-decimal) + an `mgrs` grid string — see geoCoords.js/mapPmAdapter.js's `geometryToLatLon`.
//   4. Movement — handled inside dtgDiff.js's `moved` events (distanceKm/bearingDeg/direction +
//      any explicit Z_SPEED/Q_DIRECTION_OF_MOVEMENT modifier) — nothing extra needed here.
//   5. Diff shape — dtgDiff.js now returns one flat `events` array (see its own doc comment);
//      this module's only remaining job is re-keying each event's `markingId` to `ref`.
//   6. Redundant unit fields — the units list keeps `description` (SIDC-decoded prose) plus only
//      `affiliation`/`battleDimension`/`status` as flat fields (status is also diffed — it's
//      needed for both grouping/filtering AND change-tracking, the one field that's both per the
//      review). Echelon/mobility/HQ-TF-feint/etc. are dropped as standalone unit fields now that
//      they're folded into `description` — see sidcDecoder.js.
//   7. Map image — `mapPreviewDataUrl` is never part of `payload` now; `buildSitrepPayload`
//      returns `{payload, images}`, with `images` a sibling array the caller can attach to a
//      vision-model call later. See MapNodeView.js's call site for the corresponding console.log
//      update (no more raw base64 dumped to the console either).
//   8. Nesting depth — final shape is `{report, units, changes, idMap}`, ~3 levels deep, matching
//      the review's proposed AFTER_JSON structural template.
//
// Deliberate scope gap (product decision, not an oversight): this builder does NOT apply any
// classification/compartment/distribution filtering to the markings/graphics it includes — every
// marking within the view's bbox is described regardless of its classification. Phase 1 is a
// local, log-only preview of the payload shape; classification-aware redaction is intentionally
// deferred to a later ticket (not yet scoped) once this payload is actually wired to an LLM call
// or persisted anywhere. Do not "fix" this here without a corresponding ticket — see
// MINSKY-16404's Phase 1 description.

import { featureBbox, resolveTrackAtDtg, geometryToLatLon, getEffectiveDtgSelected } from '../schema/mapPmAdapter';
import { latLonToMgrs } from '../schema/geoCoords';
import { epochMsToIso, epochMsToMilitaryDtg } from '../schema/militaryDtg';
import { buildDtgChangeHistory } from '../schema/dtgDiff';
import { decodeSidc } from '../sidc/sidcDecoder';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function geometryIntersectsBbox(geometry, bbox) {
    if (!bbox) return true;
    const bb = featureBbox(geometry);
    if (!bb) return false;
    const [sw, ne] = bbox;
    return !(bb[1][0] < sw[0] || bb[0][0] > ne[0] || bb[1][1] < sw[1] || bb[0][1] > ne[1]);
}

/**
 * Bug fix (report: "It doesn't take into json payload prev days when I explicitly say so in the
 * modal"): a marking's *current* position (at `cursor`) is what decides whether it's "in view"
 * for the units list above, but change-over-time needs a broader notion of "relevant to this
 * view" — a marking that WAS in the bbox at some point during the caller's lookback window but
 * has since moved (or the cursor has since moved) out of it must still be considered, or its
 * departure/movement is silently invisible no matter how large `windowDays` is (the window
 * control would then have no observable effect for that marking at all). Checks every snapshot
 * within `[windowStart, cursor]`, not just the resolved-at-cursor one.
 */
function markingTouchedBboxWithinWindow(marking, bbox, windowStart, cursor) {
    if (!bbox) return true;
    for (const s of Array.isArray(marking?.snapshots) ? marking.snapshots : []) {
        if (typeof s?.dtg !== 'number' || s.dtg < windowStart || s.dtg > cursor) continue;
        if (geometryIntersectsBbox(s.geometry, bbox)) return true;
    }
    return false;
}

function isEmptyValue(value) {
    if (value === null || value === undefined || value === '') return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
}

/**
 * Recursively strip null/undefined/''/[]/{} properties (and array entries) from `value`, so the
 * final logged JSON stays dense — no noise keys for an LLM to wade through. Falsy-but-meaningful
 * values (`false`, `0`) are preserved.
 *
 * @param {*} value
 * @returns {*}
 */
export function stripEmptyDeep(value) {
    if (Array.isArray(value)) {
        return value
            .map(stripEmptyDeep)
            .filter((v) => !isEmptyValue(v));
    }
    if (value !== null && typeof value === 'object') {
        const result = {};
        for (const [key, raw] of Object.entries(value)) {
            const cleaned = stripEmptyDeep(raw);
            if (!isEmptyValue(cleaned)) result[key] = cleaned;
        }
        return result;
    }
    return value;
}

function buildLayerMap(layers) {
    return new Map((Array.isArray(layers) ? layers : []).map((l) => [l.layerId, l]));
}

/**
 * Describe one resolved-and-in-view marking for the `units` list — SIDC decoded to text (ticket
 * MINSKY-16405), never the raw sidc/sidcModifiers fields; non-SIDC tactical graphics described
 * via elementType/graphicType/label instead. Coordinates are flat lat/lon + mgrs (point 3 of the
 * MINSKY-16404 review), not a nested GeoJSON geometry object.
 *
 * Per point 6 of the review, only `affiliation`/`battleDimension`/`status` are kept as standalone
 * flat fields alongside `description` — echelon/mobility/HQ-TF-feint etc. are dropped here since
 * they're already folded into `description`'s prose (decodeSidc), not duplicated.
 */
function buildUnitEntry(ref, marking, snapshot, layerMap, isCurrent) {
    const props = snapshot.props || {};
    const layer = layerMap.get(marking.layerId);
    const point = geometryToLatLon(snapshot.geometry);
    const base = {
        ref,
        layer: layer?.label || null,
        current: isCurrent,
        lat: point?.lat ?? null,
        lon: point?.lon ?? null,
        mgrs: point ? latLonToMgrs(point.lat, point.lon) : null,
    };

    if (marking.elementType === 'unit-icon' && props.sidc) {
        const decoded = decodeSidc(props.sidc, props.sidcModifiers);
        return {
            ...base,
            affiliation: decoded.affiliation,
            battleDimension: decoded.battleDimension,
            status: decoded.status,
            description: decoded.description,
        };
    }

    return {
        ...base,
        label: props.label || '',
        elementType: marking.elementType || null,
        graphicType: marking.graphicType || null,
    };
}

/** Assigns each markingId a short, stable-within-this-call `ref` ("U1", "U2", ...) the first
 * time it's seen — see this module's doc comment, point 1. Returns a `{refFor, idMap}` pair;
 * `idMap` is populated lazily as `refFor` is called. */
function createRefAssigner() {
    const idMap = {};
    const refByMarkingId = new Map();
    let nextIndex = 1;
    function refFor(markingId) {
        if (!refByMarkingId.has(markingId)) {
            const ref = `U${nextIndex++}`;
            refByMarkingId.set(markingId, ref);
            idMap[ref] = markingId;
        }
        return refByMarkingId.get(markingId);
    }
    return {refFor, idMap};
}

/**
 * @param {object} opts
 * @param {Array} [opts.markings] - `map_marking` node attrs (as from `extractMapState`).
 * @param {Array} [opts.layers] - `map_layer` node attrs.
 * @param {[[number,number],[number,number]]|null} [opts.bbox] - this embed's current view bbox;
 *   `null` means "no fit yet" and every visible marking is treated as in-view.
 * @param {number|null} [opts.selectedDtg] - this embed's selected DTG (epoch ms). `null`
 *   (never explicitly selected) resolves via `getEffectiveDtgSelected` to the latest snapshot
 *   dtg across `markings` — see that helper's doc comment — falling back to `Date.now()` only
 *   when there's no snapshot history at all yet (brand-new/empty map).
 * @param {number} [opts.windowDays=7] - lookback window (days) fed to ticket MINSKY-16406's diff
 *   utility. Caller-supplied via the button's lookback-window control — never hardcoded here.
 * @param {number|null} [opts.maxCheckpoints] - optional checkpoint cap, forwarded as-is.
 * @param {string} [opts.additionalContext] - free-text context from the button's modal.
 * @param {string} [opts.previewDataUrl] - the embed's current map preview PNG data URL. Per
 *   point 7 of the MINSKY-16404 review, this NEVER appears inside the returned `payload` — it's
 *   only surfaced via the sibling `images` array.
 * @param {{country?: string|null, state?: string|null, county?: string|null, city?: string|null, displayName?: string|null}|null} [opts.viewport]
 *   Pre-resolved reverse-geocode result for the map's visible centre (see reverseGeocode.js).
 *   When supplied, added as `payload.viewport` so the LLM can describe unit positions relative to
 *   known place names. Omitted from payload when null (graceful degradation if geocoding failed).
 * @returns {{payload: object, images: string[]}} `payload` is the final, stripped (see
 *   `stripEmptyDeep`) JSON — dense/no-noise and ready to `console.log`/send to an LLM as-is (see
 *   SitrepButton.jsx/MapNodeView.js). `images` holds zero or one preview data URL today; an array
 *   so a future multi-embed sitrep can extend it without a shape change.
 */
export function buildSitrepPayload(opts = {}) {
    const {
        markings, layers, bbox = null, selectedDtg = null,
        windowDays = 7, maxCheckpoints = null, additionalContext = '', previewDataUrl = '',
        viewport = null,
    } = opts;

    const layerMap = buildLayerMap(layers);
    // map-node-dtg-default ticket: a map node that's never had a DTG explicitly selected
    // (`selectedDtg == null`) defaults to the latest available DTG across `markings`' snapshot
    // history (see `getEffectiveDtgSelected`'s doc comment), not silently to "now" — "now" only
    // remains the fallback for the true empty-doc edge case (no snapshots to derive a latest
    // DTG from at all).
    const cursor = getEffectiveDtgSelected({markings}, selectedDtg) ?? Date.now();
    const windowStart = cursor - Math.max(windowDays, 0) * MS_PER_DAY;
    const {refFor, idMap} = createRefAssigner();

    const unitEntries = [];
    // Separate from `unitEntries`/the "current state" list above: change-over-time must consider
    // every marking that touched this view within the lookback window, not just those currently
    // in view — see `markingTouchedBboxWithinWindow`'s doc comment above.
    const changeHistoryMarkings = [];
    for (const marking of Array.isArray(markings) ? markings : []) {
        const resolved = resolveTrackAtDtg(marking, cursor);
        const isCurrentlyInView = resolved.visible && resolved.snapshot
            && geometryIntersectsBbox(resolved.snapshot.geometry, bbox);
        if (isCurrentlyInView) {
            unitEntries.push(buildUnitEntry(refFor(marking.markingId), marking, resolved.snapshot, layerMap, resolved.isCurrent));
        }
        if (isCurrentlyInView || markingTouchedBboxWithinWindow(marking, bbox, windowStart, cursor)) {
            changeHistoryMarkings.push(marking);
        }
    }

    const changeHistory = buildDtgChangeHistory(changeHistoryMarkings, cursor, {windowDays, maxCheckpoints});
    // Bug fix (report: sitrep's "Enemy Activity" change-over-time table showed the wrong DTG
    // day for every row): each event previously carried only an ISO-8601 `ts` — the LLM had
    // no pre-formatted military DTG string to copy for the table's "DTG" column (unlike
    // `report.asOfDtg` below), so it had to recompute DDHHMMZ from the ISO string itself on
    // every row. militaryDtg.js's own doc comment cites the design review's finding that
    // models are "unreliable at converting/diffing raw unix-ms" — that applies just as much to
    // reformatting an ISO string into DTG notation. `dtg` here is the exact same
    // `epochMsToMilitaryDtg` call `report.asOfDtg` uses, so the prompt can require verbatim
    // reuse (see shared-response-requirements.txt) instead of leaving room for the model to
    // independently (and unreliably) compute it.
    const events = changeHistory.events.map(({markingId, ts, type, ...fields}) => (
        {ts: epochMsToIso(ts), dtg: epochMsToMilitaryDtg(ts), ref: refFor(markingId), type, ...fields}
    ));

    const payload = stripEmptyDeep({
        report: {
            asOfIso: epochMsToIso(cursor),
            asOfDtg: epochMsToMilitaryDtg(cursor),
            generatedIso: epochMsToIso(Date.now()),
            lookbackDays: windowDays,
            analystNotes: additionalContext || '',
        },
        viewport: viewport ?? undefined,
        units: unitEntries,
        changes: {windowDays, events},
        idMap,
    });

    return {payload, images: previewDataUrl ? [previewDataUrl] : []};
}
